// COMBO PRODUCT FEATURE — swaps ONLY the settings-selected combo products for their component
// variants on every /cart/add path. Keyed strictly to #combo-variant-map, so all other products —
// including the theme's native tag-based (.custom-add-cart) combos — are untouched and behave
// exactly as before.
//
// Works at the transport level rather than per button, so it holds whichever cart is live
// (custom drawer or Shopflo) and for add-to-cart buttons the theme does not own (app blocks):
//   - fetch, with a string, URL or Request as the first argument
//   - XMLHttpRequest (jQuery, axios, older app scripts)
//   - plain <form action="/cart/add"> submits that no script intercepted
// and, for each, JSON bodies (`items` list or a single top-level `id`), FormData and
// URLSearchParams / urlencoded strings (single `id` or `items[n][id]`).
//
// Not reachable from here: /cart/{variant}:{qty} permalinks, form.submit() (fires no submit
// event), and scripts that saved their own copy of fetch before this file ran.
//
// To revert entirely: clear the "Combo products" picker in Theme Settings (instant disable), or
// delete this file + its <script> tag and the COMBO PRODUCT FEATURE block in layout/theme.liquid,
// and the "Combo Products" group in config/settings_schema.json. (assets/theme.js is NOT involved.)
(function () {
  const mapEl = document.getElementById('combo-variant-map');
  if (!mapEl) return;

  const toIds = (raw) => (raw ?? '')
    .split(',')
    .map((id) => parseInt(String(id).trim(), 10))
    .filter((id) => Number.isInteger(id) && id > 0);

  // One [data-combo-product] entry per combo product; every variant of that product maps to
  // the same component list. Entries with invalid data are skipped — a bad map would otherwise
  // POST {id: null} and 400.
  const COMBO_MAP = {};
  for (const entry of mapEl.querySelectorAll('[data-combo-product]')) {
    const comboVariantIds = toIds(entry.dataset.variantIds);
    const componentIds = toIds(entry.dataset.componentIds);
    if (!comboVariantIds.length || !componentIds.length) continue;
    for (const id of comboVariantIds) {
      COMBO_MAP[id] = componentIds;
    }
  }

  if (!Object.keys(COMBO_MAP).length) return;

  const componentsFor = (id) => COMBO_MAP[parseInt(id, 10)];

  /*
   * Published so other cart code can ask the same question without re-reading the map.
   *
   * The cart drawer's swap needs it: a swap posts to /cart/update.js, which the interceptors
   * below never see — they only watch /cart/add — so a swap into a combo would put the combo
   * itself in the cart. It expands the target through this instead.
   *
   * A copy of the ids is handed out, so a caller cannot edit the map everything else reads.
   */
  window.ComboProducts = {
    componentsFor: (id) => {
      const components = componentsFor(id);
      return components ? [...components] : null;
    },
  };

  /* ---------- request shape helpers ---------- */

  // Matches /cart/add and /cart/add.js, with or without a locale prefix (/en/cart/add).
  const isAddUrl = (url) => {
    try {
      return /\/cart\/add(\.js)?\/?$/.test(new URL(String(url), window.location.origin).pathname);
    } catch (_) {
      return false;
    }
  };

  // Resolve the /cart/add.js endpoint from any /cart/add URL, keeping locale prefix and query.
  const toAddJsUrl = (url) => {
    const target = new URL(String(url), window.location.origin);
    if (!target.pathname.endsWith('.js')) target.pathname = target.pathname.replace(/\/?$/, '.js');
    return target.toString();
  };

  // Replaces each combo line with its component lines. Returns null when nothing matched, so
  // callers can leave the request exactly as it was.
  const expandLines = (lines) => {
    let modified = false;
    const expanded = [];
    for (const line of lines) {
      const components = componentsFor(line?.id);
      if (!components) {
        expanded.push(line);
        continue;
      }
      const quantity = parseInt(line.quantity, 10) || 1;
      for (const id of components) expanded.push({ id, quantity });
      modified = true;
    }
    return modified ? expanded : null;
  };

  // JSON body, either { items: [...] } or a single line { id, quantity, ... } (e.g. PDP Buy now).
  // Returns the rewritten body object, or null when it holds no combo variant.
  const rewriteJsonBody = (text) => {
    let body;
    try {
      body = JSON.parse(text);
    } catch (_) {
      return null;
    }
    if (!body || typeof body !== 'object') return null;

    if (body.items) {
      const lines = Array.isArray(body.items) ? body.items : Object.values(body.items);
      const items = expandLines(lines);
      return items ? { ...body, items } : null;
    }

    if (body.id) {
      const { id, quantity, properties, selling_plan, ...rest } = body;
      const items = expandLines([{ id, quantity, properties, selling_plan }]);
      return items ? { ...rest, items } : null;
    }

    return null;
  };

  // FormData / URLSearchParams body, either a single `id` or `items[n][id]` lines.
  // Returns an equivalent JSON body object, or null when it holds no combo variant.
  const rewriteFormBody = (params) => {
    const lines = new Map();
    const lineFor = (index) => {
      if (!lines.has(index)) lines.set(index, {});
      return lines.get(index);
    };
    const extra = {};

    for (const [key, value] of params.entries()) {
      if (value === '' || typeof value !== 'string') continue;
      const itemKey = key.match(/^items\[(\d*)\]\[([^\]]+)\](?:\[([^\]]+)\])?$/);
      const singleKey = key.match(/^(id|quantity|selling_plan)$|^properties\[([^\]]+)\]$/);
      if (itemKey) {
        const line = lineFor(`i${itemKey[1]}`);
        if (itemKey[2] === 'properties' && itemKey[3]) (line.properties ??= {})[itemKey[3]] = value;
        else line[itemKey[2]] = value;
      } else if (singleKey) {
        const line = lineFor('single');
        if (singleKey[2]) (line.properties ??= {})[singleKey[2]] = value;
        else line[singleKey[1]] = value;
      } else if (key === 'sections') {
        extra.sections = value.split(',').filter(Boolean);
      } else if (key === 'sections_url') {
        extra.sections_url = value;
      }
    }

    const items = expandLines([...lines.values()].filter((line) => line.id));
    return items ? { ...extra, items } : null;
  };

  // Any supported body → rewritten JSON body object, or null to leave the request alone.
  const rewriteBody = (body) => {
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (trimmed.startsWith('{')) return rewriteJsonBody(trimmed);
      return rewriteFormBody(new URLSearchParams(trimmed));
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) return rewriteFormBody(body);
    if (body instanceof URLSearchParams) return rewriteFormBody(body);
    return null;
  };

  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* ---------- fetch ---------- */

  const originalFetch = window.fetch;

  const sendRewritten = (url, rewritten, init) => originalFetch.call(window, toAddJsUrl(url), {
    method: 'POST',
    credentials: init?.credentials ?? 'same-origin',
    headers: JSON_HEADERS,
    body: JSON.stringify(rewritten)
  });

  window.fetch = function patchedFetch(input, init) {
    try {
      const isRequest = typeof Request !== 'undefined' && input instanceof Request;
      const url = isRequest ? input.url : String(input ?? '');
      const method = String(init?.method ?? (isRequest ? input.method : 'GET')).toUpperCase();

      if (method === 'POST' && isAddUrl(url)) {
        if (init?.body != null) {
          const rewritten = rewriteBody(init.body);
          if (rewritten) return sendRewritten(url, rewritten, init);
        } else if (isRequest) {
          // Request bodies are streams: read a copy, and send the original untouched if it is
          // not a combo add.
          return input.clone().text().then((text) => {
            const rewritten = rewriteBody(text);
            return rewritten
              ? sendRewritten(url, rewritten, { credentials: input.credentials })
              : originalFetch.call(window, input, init);
          });
        }
      }
    } catch (_) {
      /* never let the swap break a real request */
    }
    return originalFetch.apply(this, arguments);
  };

  /* ---------- XMLHttpRequest ---------- */

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
    this.__comboMethod = String(method ?? '').toUpperCase();
    this.__comboUrl = url;
    return originalOpen.apply(this, arguments);
  };

  // JSON body object → items[n][...] form fields, for requests that must stay form-encoded.
  const toFormEntries = (rewritten) => {
    const entries = [];
    rewritten.items.forEach((line, index) => {
      for (const [key, value] of Object.entries(line)) {
        if (value == null) continue;
        if (key === 'properties') {
          for (const [name, prop] of Object.entries(value)) entries.push([`items[${index}][properties][${name}]`, prop]);
        } else {
          entries.push([`items[${index}][${key}]`, value]);
        }
      }
    });
    if (rewritten.sections) entries.push(['sections', [].concat(rewritten.sections).join(',')]);
    if (rewritten.sections_url) entries.push(['sections_url', rewritten.sections_url]);
    return entries;
  };

  // The rewritten body in the same format the caller sent. open() has fixed the URL and any
  // Content-Type already set cannot be taken back, so the body has to keep matching it.
  const inOriginalFormat = (original, rewritten) => {
    if (typeof original === 'string') {
      return original.trim().startsWith('{')
        ? JSON.stringify(rewritten)
        : new URLSearchParams(toFormEntries(rewritten)).toString();
    }
    if (original instanceof URLSearchParams) return new URLSearchParams(toFormEntries(rewritten));
    const form = new FormData();
    for (const [key, value] of toFormEntries(rewritten)) form.append(key, value);
    return form;
  };

  XMLHttpRequest.prototype.send = function patchedSend(body) {
    try {
      if (this.__comboMethod === 'POST' && isAddUrl(this.__comboUrl) && body != null) {
        const rewritten = rewriteBody(body);
        if (rewritten) return originalSend.call(this, inOriginalFormat(body, rewritten));
      }
    } catch (_) {
      /* fall through to a normal send */
    }
    return originalSend.apply(this, arguments);
  };

  /* ---------- plain form submits ---------- */

  // Registered on window so it runs after every form and document listener: by then, a
  // submit nobody prevented is about to post the browser straight to /cart/add.
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
    if ((form.method || 'get').toUpperCase() !== 'POST' || !isAddUrl(form.action)) return;

    const rewritten = rewriteFormBody(new FormData(form, event.submitter ?? undefined));
    if (!rewritten) return;

    event.preventDefault();
    sendRewritten(form.action, rewritten).finally(() => {
      // Same destination a native /cart/add post redirects to.
      window.location.href = `${window.Shopify?.routes?.root ?? '/'}cart`;
    });
  });
})();
